import React from 'react';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useLcaProductCertificates } from 'src/Lca/hooks';
import { useTranslation } from 'react-i18next';
import { DeleteOutline, Save } from '@mui/icons-material';
import { LcaProductCertificateOutSchema } from 'src/Lca/types';
import { toast } from 'react-toastify';

interface CertificationTableEntryProps {
  entry: Partial<LcaProductCertificateOutSchema>;
  onSave?: () => void;
}

function CertificationTableEntry({
  entry,
  onSave,
}: Readonly<CertificationTableEntryProps>) {
  const { t } = useTranslation('lca', { keyPrefix: 'certifications' });
  const { create, update, _delete } = useLcaProductCertificates({
    product_id: entry.product_id as number,
  });
  const [form, setForm] = React.useState(entry);
  const [isModified, setIsModified] = React.useState(false);
  return (
    <TableRow>
      <TableCell>
        <StyledTextField
          fullWidth
          value={form.name}
          onChange={(event) => {
            setForm({ ...form, name: event.target.value });
            setIsModified(true);
          }}
        />
      </TableCell>
      <TableCell>
        <StyledTextField
          fullWidth
          minRows={3}
          multiline
          value={form.description}
          onChange={(event) => {
            setForm({ ...form, description: event.target.value });
            setIsModified(true);
          }}
        />
      </TableCell>
      <TableCell>
        <IconButton
          disabled={!isModified}
          onClick={() => {
            const onSuccess = () => {
              setIsModified(false);
              onSave?.();
            };
            const onError = () => {
              toast.error(t('error') as string);
            };

            if (form.id === undefined) {
              create.mutate(form, { onSuccess, onError });
            } else {
              update.mutate(form, {
                onSuccess,
                onError,
              });
            }
          }}
        >
          <Save />
        </IconButton>
      </TableCell>
      <TableCell>
        {entry.id !== undefined && (
          <IconButton
            onClick={() => {
              _delete.mutate(entry.id as number);
            }}
          >
            <DeleteOutline />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function Certifications({ values }: ProductInformationProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'certifications' });
  const { _data } = useLcaProductCertificates({
    product_id: values.id,
  });
  const [newRow, setNewRow] = React.useState<boolean>(false);

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell width="20%">{t('name')}</TableCell>
          <TableCell width="80%">{t('description')}</TableCell>
          <TableCell colSpan={2} />
        </TableRow>
      </TableHead>

      <TableBody>
        {_data?.map((entry) => (
          <CertificationTableEntry key={entry.id} entry={entry} />
        ))}
        {newRow && (
          <CertificationTableEntry
            entry={{ product_id: values.id }}
            onSave={() => setNewRow(false)}
          />
        )}
        {!newRow && (
          <TableRow>
            <TableCell colSpan={4}>
              <Button variant="secondary" onClick={() => setNewRow(true)}>
                {t('addCertificate')}
              </Button>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
